// Standalone Qualifying Events admin surface. Renders the dashboard
// card list, the New Event modal, and the per-event detail page mounted
// at /admin/qualifying/:eventId. Lives outside /tournaments — the only
// data this surface touches is /qualifyingEvents.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { get, ref, update } from 'firebase/database'
import { getFunctions, httpsCallable } from 'firebase/functions'

import { categorizeImport } from '../lib/ggImport.js'
import AIAssistant from '../components/AIAssistant.jsx'
import TeamJoinCard from './TeamJoinCard.jsx'
import {
  approveAllTeams,
  assignQualifyingEventTokens,
  createQualifyingEvent,
  createQualifyingEventFromRegistration,
  deleteQualifyingEvent,
  applyQualifyingImport,
  setQualifyingEventStatus,
  setQualifyingEventLocked,
  submitQualifyingEventResult,
  watchQualifyingEvent,
  watchQualifyingEvents,
} from '../lib/qualifyingEvents.js'
import {
  bulkAddRegistrations,
  createTournament,
  updateTournamentInfo,
  watchTournaments,
} from '../lib/data.js'
import EventTitle, { splitEventTitle } from '../components/EventTitle.jsx'
import { app, db } from '../lib/firebase.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'
import { Modal, Field } from './AdminApp.jsx'
import { buildTournamentFromQualifying } from '../lib/buildTournament.js'
import { DEFAULT_PARS, totalPar } from '../lib/scoring.js'
import CLUB from '../config/club.js'

const ACCENT = CLUB.accentColor || '#123D26'
const PRIMARY = CLUB.primaryColor || '#123D26'

// Format choices for the New Qualifying Event modal. PBCC 4-Ball first
// because that's the flagship event this whole flow was built for.
const QE_FORMATS = [
  '4-Ball Best Ball',
  'Stroke Play Individual',
  'Scramble',
  'Shamble',
  'Alternate Shot',
  'Stableford',
]

// ── Dashboard section ─────────────────────────────────────────────────
// Drops into AdminHome alongside My Tournaments / Demos. Shows a list
// of events (sorted newest-first) and a New Qualifying Event button.
export function QualifyingEventsSection() {
  const [events, setEvents] = useState({})
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const off = watchQualifyingEvents(setEvents)
    return () => off()
  }, [])

  // The 4-Ball sidebar "New Qualifying Event" tab triggers the create flow here.
  useEffect(() => {
    const open = () => setCreating(true)
    window.addEventListener('pbcc4ball:new-qualifying', open)
    return () => window.removeEventListener('pbcc4ball:new-qualifying', open)
  }, [])

  const list = useMemo(() => {
    return Object.entries(events || {})
      .map(([id, v]) => ({
        id,
        name: v?.info?.name || '(unnamed)',
        date: v?.info?.date || null,
        endDate: v?.info?.endDate || null,
        format: v?.info?.format || '',
        status: v?.info?.status || 'open',
        builtTournamentId: v?.info?.builtTournamentId || null,
        createdAt: v?.info?.createdAt || 0,
        teamCount: v?.teams ? Object.keys(v.teams).length : 0,
        submittedCount: v?.teams
          ? Object.values(v.teams).filter((t) => t?.submittedAt).length
          : 0,
      }))
      // Once built into a tournament, the qualifying event has advanced —
      // drop it here so it only lives under My Tournaments (New Tournament).
      .filter((q) => !q.builtTournamentId)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  }, [events])

  return (
    <section>
      <div className="mb-3">
        <div className="min-w-0">
          <span className="pbcc-gold-rule" aria-hidden="true" style={PBCC_PREMIUM ? { backgroundColor: '#C2A24E' } : FOURBALL_ONLY ? { backgroundColor: '#FFFFFF' } : undefined} />
          <h2 className="pbcc-h2 text-xl mt-1" style={(FOURBALL_ONLY || PBCC_PREMIUM) ? { color: '#FFFFFF' } : undefined}>Qualifying Events</h2>
          <p className={`text-sm mt-0.5 leading-snug ${FOURBALL_ONLY ? 'text-white' : 'text-gray-600'}`}>
            Lists sent here from Registration arrive already loaded, with name, dates, and teams inherited. Run qualifying, then <b>Approve &amp; Lock</b> the field — only then can it be built into a tournament.
          </p>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="pbcc-empty-card min-h-[220px]">
          <div
            className="hidden sm:flex w-52 md:w-64 lg:w-80 shrink-0 items-center justify-center relative"
            style={{
              backgroundImage: `url(/pbcc-flag.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'contrast(1.08) saturate(1.12)',
            }}>
          </div>
          <div className="px-5 py-6 sm:px-8 flex flex-col gap-1.5 justify-center">
            <h3 className="text-lg font-extrabold text-gray-900">No qualifying events yet.</h3>
            <p className="text-sm text-gray-600 leading-snug max-w-md">
              Use <b>New Qualifying Event</b> in the sidebar to create one. Players scan a QR code to enter their scores; you approve the field and create the tournament from there.
            </p>
          </div>
        </div>
      ) : (
        <div className={PBCC_PREMIUM ? 'pbcc-card-grid' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
          {list.map((q) => <QualifyingEventCard key={q.id} q={q} />)}
        </div>
      )}

      {creating ? (
        <CreateEventModal onClose={() => setCreating(false)} />
      ) : null}
    </section>
  )
}

function QualifyingEventCard({ q }) {
  const created = q.createdAt ? new Date(q.createdAt).toLocaleDateString() : '—'
  return (
    <Link
      to={`/admin/qualifying/${q.id}`}
      className="block rounded-2xl p-4 transition active:scale-[0.99]"
      style={{
        backgroundColor: PBCC_PREMIUM ? '#F7F2E6' : '#FFFFFF',
        border: PBCC_PREMIUM ? '1.5px solid #C2A24E' : `1px solid ${ACCENT}55`,
        boxShadow: PBCC_PREMIUM ? '0 10px 28px rgba(0,0,0,0.28)' : '0 6px 18px rgba(0,0,0,0.08)',
        textDecoration: 'none',
      }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {(() => {
            const { nameLines, dateLine } = splitEventTitle({ name: q.name, date: q.date, endDate: q.endDate })
            return (
              <>
                <h3 className="font-extrabold text-base" style={{ color: PRIMARY, lineHeight: 1.2 }}>
                  {(nameLines.length ? nameLines : [q.name]).map((l, i) => <div key={i}>{l}</div>)}
                </h3>
                {dateLine ? <p className="text-sm font-semibold mt-0.5" style={{ color: ACCENT }}>{dateLine}</p> : null}
              </>
            )
          })()}
        </div>
        <StatusBadge status={q.status} />
      </div>
      <p className="text-sm text-gray-600 mt-1">{q.format}</p>
      <div className="mt-3 flex items-baseline gap-3 text-sm" style={{ color: PRIMARY }}>
        <span><b>{q.submittedCount}</b> submitted</span>
        <span className="text-gray-500">·</span>
        <span><b>{q.teamCount}</b> total teams</span>
      </div>
      <p className="text-sm text-gray-500 mt-2">Created {created}</p>
    </Link>
  )
}

function StatusBadge({ status }) {
  const open = status !== 'closed'
  return (
    <span
      className="text-sm font-extrabold uppercase tracking-widest rounded-full px-2 py-0.5"
      style={PBCC_PREMIUM ? {
        backgroundColor: '#123D26',
        color: '#F7F2E6',
        border: '1px solid rgba(194,162,78,0.75)',
      } : {
        backgroundColor: open ? 'rgba(34,168,90,0.12)' : 'rgba(0,0,0,0.06)',
        color: open ? '#15803D' : '#6B7280',
        border: `1px solid ${open ? '#15803D55' : '#6B728055'}`,
      }}>
      {open ? 'Open' : 'Closed'}
    </span>
  )
}

// New Qualifying Event = pick a registration that needs to qualify (mirrors New
// Tournament's BuildPicker). Picking one creates the linked qualifying event
// seeded from it (same result as Registration's "Send to Qualifying"). A small
// "fresh standalone round" fallback covers the rare no-source case.
function CreateEventModal({ onClose }) {
  const navigate = useNavigate()
  const [tournaments, setTournaments] = useState({})
  const [sel, setSel] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [fresh, setFresh] = useState(false)
  const [name, setName] = useState('PBCC 4-Ball 2026')
  const [format, setFormat] = useState(QE_FORMATS[0])

  useEffect(() => {
    const off = watchTournaments(setTournaments)
    return () => off()
  }, [])

  // Registrations marked "needs to qualify" that haven't been sent yet.
  const ready = useMemo(() => {
    const out = []
    for (const [id, v] of Object.entries(tournaments || {})) {
      if (!v || typeof v !== 'object') continue
      const info = v.info || {}
      if ((info.isDemo && !info.isPractice) || info.isTestStation) continue // practice field flows through
      if ((info.stage || 'registration') !== 'registration') continue
      if (info.status === 'closed') continue
      if (info.needsQualifying === false) continue // default ON; only explicit false skips
      if (info.qualifyingEventId) continue // already sent
      const count = v.registrations ? Object.keys(v.registrations).length : 0
      out.push({ id, info, count })
    }
    return out.sort((a, b) => (b.info.createdAt || 0) - (a.info.createdAt || 0))
  }, [tournaments])

  const selItem = ready.find((r) => r.id === sel) || null
  const firstLine = (n) => String(n || '(unnamed)').split('\n')[0]

  const onSend = async () => {
    if (!selItem) return
    setBusy(true); setErr('')
    try {
      const regs = tournaments?.[selItem.id]?.registrations || {}
      const eventId = await createQualifyingEventFromRegistration(selItem.id, selItem.info, regs)
      await updateTournamentInfo(selItem.id, { qualifyingEventId: eventId })
      onClose()
      navigate(`/admin/qualifying/${eventId}`)
    } catch (e) {
      setErr(e?.message || 'Could not send to Qualifying.')
      setBusy(false)
    }
  }

  const onCreateFresh = async () => {
    setBusy(true); setErr('')
    try {
      const eventId = await createQualifyingEvent({ name, format })
      onClose()
      navigate(`/admin/qualifying/${eventId}`)
    } catch (e) {
      setErr(e?.message || 'Could not create event.')
      setBusy(false)
    }
  }

  return (
    <Modal title="New Qualifying Event" onClose={onClose} size="lg">
      <div className="space-y-3">
        {!fresh ? (
          <>
            {ready.length > 0 ? (
              <>
                <p className="text-sm text-gray-700">
                  Pick a registration marked <b>needs to qualify</b>. Its name, dates, and teams carry over — no re-entry.
                </p>
                <select className="input" value={sel} onChange={(e) => setSel(e.target.value)} autoFocus>
                  <option value="">Choose a registration…</option>
                  {ready.map((r) => (
                    <option key={r.id} value={r.id}>
                      {firstLine(r.info.name)} — {r.count} team{r.count === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
                {selItem ? (
                  <div className="rounded-xl px-5 py-4" style={{ border: '1px solid rgba(18, 61, 38,0.5)', background: '#FFFFFF' }}>
                    <EventTitle info={selItem.info} label="Send to Qualifying" />
                  </div>
                ) : null}
                <button type="button" className="btn-primary w-full" disabled={!sel || busy} onClick={onSend}>
                  {busy ? 'Sending…' : 'Send to Qualifying →'}
                </button>
                <div className="text-center text-sm text-gray-400">— or —</div>
              </>
            ) : (
              <p className="text-sm text-gray-600 leading-snug">
                No registrations are waiting to qualify. In <b>Registration</b>, turn on <b>“needs to qualify”</b> for a list — it then shows up here. Or start a fresh standalone round.
              </p>
            )}
            <button type="button" className="btn-secondary w-full" onClick={() => setFresh(true)}>
              Start a fresh standalone qualifying round
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 leading-snug">
              Standalone round — players self-register to qualify (no source registration).
            </p>
            <Field label="Event Name">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g. PBCC 4-Ball 2026" autoFocus />
            </Field>
            <Field label="Format">
              <select value={format} onChange={(e) => setFormat(e.target.value)} className="input">
                {QE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <div className="flex gap-2 pt-1">
              <button type="button" className="btn-ghost text-sm" onClick={() => setFresh(false)} disabled={busy}>← Back</button>
              <button type="button" className="btn-primary text-sm flex-1" onClick={onCreateFresh} disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create standalone round'}
              </button>
            </div>
          </>
        )}
        {err ? <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>{err}</p> : null}
      </div>
    </Modal>
  )
}

// ── Detail page ───────────────────────────────────────────────────────
// Route: /admin/qualifying/:eventId. Shows the scan QR, the live team
// list (sorted by score asc, pending at bottom), and the admin
// controls — Approve All, Close, Create-Tournament.
// TEST: per-team qualify QR codes shown on screen (and printable) inside the
// qualifying event. Each QR encodes /play?e=<eventId>&tk=<token> so a player
// scans their team's code and drops straight into the real token-bound qualify
// flow — no pasted link.
function TeamQrCodesSection({ eventId, origin, teams, teamsMap, eventName }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)   // team object → its modal card
  const [printScope, setPrintScope] = useState('') // '' | 'one' | 'all'
  const [exporting, setExporting] = useState(false) // rendering the cards for the PDF
  const [exportProgress, setExportProgress] = useState(null) // { done, total } while exporting
  const withToken = teams.filter((t) => t.accessToken).length
  // Sort the print/export lists A–Z by the FIRST player's last name (e.g.
  // "Langford / Gunter" sorts under "L" for Langford). Falls back to teamName.
  const firstLast = (t) => {
    const p1 = String(t.player1 || '').trim()
    return (p1 ? p1.split(/\s+/).pop() : String(t.teamName || t.id || '')).toLowerCase()
  }
  const withCodeTeams = teams.filter((t) => t.accessToken)
    .sort((a, b) => firstLast(a).localeCompare(firstLast(b), undefined, { numeric: true, sensitivity: 'base' }))
  const missing = teams.length - withToken
  const teamLink = (tok) => `${origin}/play?e=${encodeURIComponent(eventId)}&tk=${encodeURIComponent(tok)}`
  const labelOf = (t) => [t.player1, t.player2].filter(Boolean).map((n) => String(n).trim().split(/\s+/).pop()).join(' / ') || t.teamName || t.id
  const linkOf = (t) => (t.accessToken ? teamLink(t.accessToken) : '')
  const generate = async () => {
    setBusy(true); setMsg('')
    try {
      const n = await assignQualifyingEventTokens(eventId, teamsMap)
      setMsg(n > 0 ? `Created ${n} new code${n === 1 ? '' : 's'}.` : 'Every team already has a code.')
    } catch (e) {
      setMsg(e?.message || 'Could not generate codes.')
    }
    setBusy(false)
  }
  // Auto-generate a code for every team that lacks one (idempotent; the ref
  // guards against re-firing while a generate is in flight).
  const autoGenRef = useRef(false)
  useEffect(() => {
    if (missing > 0 && !autoGenRef.current) {
      autoGenRef.current = true
      Promise.resolve(assignQualifyingEventTokens(eventId, teamsMap))
        .catch(() => {})
        .finally(() => { autoGenRef.current = false })
    }
  }, [missing, eventId]) // eslint-disable-line react-hooks/exhaustive-deps
  // Print scope decides WHICH cards print: 'all' = the hidden all-cards sheet,
  // 'one' = just the open modal card. Reset once the print dialog closes.
  useEffect(() => {
    const reset = () => setPrintScope('')
    window.addEventListener('afterprint', reset)
    return () => window.removeEventListener('afterprint', reset)
  }, [])
  const doPrint = (scope) => { setPrintScope(scope); setTimeout(() => { try { window.print() } catch { /* ignore */ } }, 120) }

  // Export ALL team cards as ONE downloadable PDF — one page per team. Drawn with
  // jsPDF NATIVE text (perfect spacing, no html2canvas) + each team's QR image
  // from an off-screen QRCodeCanvas. Reliable download; text is always readable.
  // Keeps the existing Print all (which prints the branded HTML card).
  const exportAllPdf = async () => {
    if (withCodeTeams.length === 0) return
    setMsg(''); setExportProgress({ done: 0, total: withCodeTeams.length }); setExporting(true)
    // Let the off-screen QR canvases mount + paint before we read them.
    await new Promise((r) => setTimeout(r, 400))
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageW = doc.internal.pageSize.getWidth()   // 612
      const M = 48
      const cx = pageW / 2
      const boxX = M, boxW = pageW - 2 * M, qrSize = 148
      let placed = 0
      for (const t of withCodeTeams) {
        const canvas = document.querySelector(`[data-eqr="${t.id}"] canvas`)
        const qr = canvas ? canvas.toDataURL('image/png') : null
        if (placed > 0) doc.addPage()
        doc.setTextColor(0, 0, 0)
        let y = 70
        doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
        doc.text('PINE BLUFF COUNTRY CLUB', cx, y, { align: 'center' }); y += 30
        doc.setFontSize(32); doc.text('PBCC 4-Ball', cx, y, { align: 'center' }); y += 26
        doc.setFontSize(17); doc.text(String(t.teamName ?? ''), cx, y, { align: 'center' }); y += 4
        const players = [t.player1, t.player2].filter(Boolean).join('   /   ')
        if (players) { y += 16; doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.text(players, cx, y, { align: 'center' }) }
        y += 18; doc.setLineWidth(2.5); doc.line(M, y, pageW - M, y); y += 26

        const drawStep = (label, who, lines, note) => {
          const boxH = 182
          doc.setDrawColor(0); doc.setLineWidth(1.5); doc.roundedRect(boxX, y, boxW, boxH, 12, 12, 'S')
          doc.setFillColor(0, 0, 0); doc.rect(boxX + 16, y + 16, 74, 22, 'F')
          doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(13)
          doc.text(label, boxX + 16 + 37, y + 31, { align: 'center' })
          doc.setTextColor(0, 0, 0); doc.setFontSize(20); doc.text(who, boxX + 16, y + 62)
          doc.setFont('helvetica', 'normal'); doc.setFontSize(12)
          let ly = y + 88
          for (const ln of lines) { doc.text(ln, boxX + 16, ly, { maxWidth: boxW - qrSize - 56 }); ly += 18 }
          if (qr) doc.addImage(qr, 'PNG', boxX + boxW - qrSize - 16, y + 17, qrSize, qrSize)
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(70, 70, 70)
          doc.text(note, boxX + boxW - qrSize / 2 - 16, y + 17 + qrSize + 11, { align: 'center', maxWidth: qrSize + 6 })
          doc.setTextColor(0, 0, 0)
          y += boxH + 18
        }
        drawStep('STEP 1', 'EVERYONE', ['Scan this QR code FIRST — iPhone & Android.', '', 'ANDROID USERS: you are DONE after this step.'], 'Everyone scans first – iPhone & Android')
        drawStep('STEP 2', 'iPHONE ONLY', ['1.  Open the app', '2.  Tap QR Scan', '3.  Scan this QR code'], 'iPhone only – open app, tap QR Scan, then scan')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11)
        doc.text('Android users are finished after Step 1.  iPhone users: complete Step 2.', cx, y + 6, { align: 'center', maxWidth: boxW })

        placed++
        setExportProgress({ done: placed, total: withCodeTeams.length })
      }
      const safe = String(eventName || 'qualifying').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'qualifying'
      doc.save(`${safe}_team_cards.pdf`)
      setMsg(`Exported ${placed} team card${placed === 1 ? '' : 's'} — one per page.`)
    } catch (e) {
      setMsg(e?.message || 'Could not export the PDF.')
    } finally {
      setExporting(false); setExportProgress(null)
    }
  }

  // Download ALL teams as ONE PDF of COMPACT units: team name on top + its TWO QR
  // codes side by side (small "SCAN THIS CODE FIRST/SECOND" labels, no instruction
  // paragraphs). 6 teams per page (2×3). One file, every team, downloaded — reuses
  // the same off-screen QR canvases as exportAllPdf.
  const downloadCompactPdf = async () => {
    if (withCodeTeams.length === 0) return
    setMsg(''); setExportProgress({ done: 0, total: withCodeTeams.length }); setExporting(true)
    await new Promise((r) => setTimeout(r, 400))
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageW = doc.internal.pageSize.getWidth()   // 612
      const pageH = doc.internal.pageSize.getHeight()  // 792
      const M = 28, cols = 2, rows = 3, perPage = cols * rows // 6 teams/page
      const colW = (pageW - 2 * M) / cols
      const rowH = (pageH - 2 * M) / rows
      const qrSize = 108, gap = 14
      let placed = 0
      for (const t of withCodeTeams) {
        const canvas = document.querySelector(`[data-eqr="${t.id}"] canvas`)
        const qr = canvas ? canvas.toDataURL('image/png') : null
        const idx = placed % perPage
        if (placed > 0 && idx === 0) doc.addPage()
        const c = idx % cols, r = Math.floor(idx / cols)
        const cellX = M + c * colW, cellY = M + r * rowH
        // team name
        doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(14)
        doc.text(String(labelOf(t)), cellX + colW / 2, cellY + 24, { align: 'center', maxWidth: colW - 14 })
        // two QR codes side by side (same team link, shown twice = Step 1 & Step 2)
        const pairW = qrSize * 2 + gap
        const qx1 = cellX + (colW - pairW) / 2
        const qx2 = qx1 + qrSize + gap
        const qy = cellY + 40
        if (qr) { doc.addImage(qr, 'PNG', qx1, qy, qrSize, qrSize); doc.addImage(qr, 'PNG', qx2, qy, qrSize, qrSize) }
        // small labels under each code
        // STEP header (bold, prominent) then the readable device label under each QR
        doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0); doc.setFontSize(13.5)
        doc.text('STEP 1', qx1 + qrSize / 2, qy + qrSize + 16, { align: 'center' })
        doc.text('STEP 2', qx2 + qrSize / 2, qy + qrSize + 16, { align: 'center' })
        doc.setFontSize(11); doc.setTextColor(20, 20, 20)
        doc.text('iPhone and Android', qx1 + qrSize / 2, qy + qrSize + 31, { align: 'center', maxWidth: qrSize + 10 })
        doc.text('iPhone Only', qx2 + qrSize / 2, qy + qrSize + 31, { align: 'center', maxWidth: qrSize + 10 })
        placed++
        setExportProgress({ done: placed, total: withCodeTeams.length })
      }
      const safe = String(eventName || 'qualifying').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'qualifying'
      doc.save(`${safe}_team_QR_compact.pdf`)
      setMsg(`Downloaded ${placed} team${placed === 1 ? '' : 's'} — 6 per page, name + both QR codes.`)
    } catch (e) {
      setMsg(e?.message || 'Could not build the PDF.')
    } finally {
      setExporting(false); setExportProgress(null)
    }
  }

  const q = query.trim().toLowerCase()
  const filtered = q
    ? teams.filter((t) => `${t.teamName || ''} ${t.player1 || ''} ${t.player2 || ''}`.toLowerCase().includes(q))
    : teams

  return (
    <div className="mt-6">
      <style>{'.tqp-all { display: none; }'}</style>
      {printScope ? (
        <style>{`@media print {
          body * { visibility: hidden !important; }
          .tqp[data-scope="all"] .tqp-all, .tqp[data-scope="all"] .tqp-all * { visibility: visible !important; }
          .tqp[data-scope="all"] .tqp-all { display: block !important; position: absolute; left: 0; top: 0; width: 100%; }
          .tqp[data-scope="one"] .tqp-modal-card, .tqp[data-scope="one"] .tqp-modal-card * { visibility: visible !important; }
          .tqp[data-scope="one"] .tqp-modal-card { position: absolute; left: 0; top: 0; width: 100%; display: flex !important; justify-content: center; }
          .team-join-card { page-break-after: always; break-after: page; }
          .team-join-card:last-child { page-break-after: auto; break-after: auto; }
        }`}</style>
      ) : null}
      <div className="tqp" data-scope={printScope}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 no-print">
          <span className="font-extrabold text-base flex items-center gap-2" style={{ color: PRIMARY }}>
            🔑 Team Qualify QR Codes
            <span className="text-sm font-semibold text-gray-500">({teams.length})</span>
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">{withToken}/{teams.length} have a code</span>
            <button onClick={generate} disabled={busy} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: PRIMARY, color: '#FFFFFF' }}>
              {busy ? 'Generating…' : 'Generate missing codes'}
            </button>
            <button onClick={() => doPrint('all')} disabled={withToken === 0} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: ACCENT, color: PRIMARY }}>🖨 Print all</button>
            <button onClick={exportAllPdf} disabled={exporting || withToken === 0} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: '#FFFFFF', color: PRIMARY, border: `1.5px solid ${PRIMARY}` }}>{exporting ? 'Exporting…' : '⬇ Export All (PDF)'}</button>
            <button onClick={downloadCompactPdf} disabled={exporting || withToken === 0} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: PRIMARY, color: '#FFFFFF' }} title="One PDF, all teams — each = name + both QR codes, 6 teams per page">{exporting ? 'Building…' : '⬇ Download All · 6/page'}</button>
          </div>
        </div>

        {(<>
          {msg ? <p className="text-sm text-gray-600 mb-2 no-print">{msg}</p> : null}
          <p className="text-sm text-gray-600 mb-3 no-print"><b>Download All · 6/page</b> makes ONE PDF of every team — each = team name + both QR codes side by side (SCAN FIRST / SECOND), 6 teams per page, no instructions. (Or: click a team for its full STEP 1 / STEP 2 card, <b>Print all</b> for branded cards, <b>Export All (PDF)</b> for one full card per page.)</p>

          {teams.length === 0 ? (
            <p className="text-sm text-gray-600 italic no-print">No teams yet — teams appear here once the field is loaded.</p>
          ) : (
            <div className="no-print">
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search teams by name…"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-72 mb-2" />
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-500 italic px-1 py-2">No team matches “{query}”.</p>
              ) : (
                <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {filtered.map((t) => (
                    <li key={t.id}>
                      <button type="button" onClick={() => setSelected(t)} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="font-bold" style={{ color: PRIMARY }}>{labelOf(t)}</span>
                          {(t.player1 || t.player2) ? <span className="text-sm text-gray-500 ml-2">{[t.player1, t.player2].filter(Boolean).join(' & ')}</span> : null}
                        </span>
                        <span className="text-sm font-semibold shrink-0" style={{ color: t.accessToken ? '#047857' : '#B45309' }}>{t.accessToken ? 'View card ›' : 'code pending…'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Per-team modal — big STEP 1/STEP 2 card + Print this card */}
          {selected ? (
            <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 60, padding: 20, overflow: 'auto' }}>
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 24, marginBottom: 24 }}>
                <div className="tqp-modal-card">
                  {linkOf(selected)
                    ? <TeamJoinCard teamLabel={labelOf(selected)} step1Url={linkOf(selected)} step2Url={linkOf(selected)} />
                    : <div className="bg-white rounded-2xl p-8 text-center font-semibold" style={{ color: '#123D26' }}>Code is still generating for this team — try again in a moment.</div>}
                </div>
                <div className="no-print" style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'center' }}>
                  {linkOf(selected) ? <button onClick={() => doPrint('one')} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ background: ACCENT, color: PRIMARY }}>🖨 Print this card</button> : null}
                  <button onClick={() => setSelected(null)} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: PRIMARY, border: `1px solid ${PRIMARY}` }}>Close</button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Hidden all-cards sheet — printed only when Print all is used. */}
          <div className="tqp-all">
            {withCodeTeams.map((t) => (
              <TeamJoinCard key={t.id} teamLabel={labelOf(t)} step1Url={linkOf(t)} step2Url={linkOf(t)} />
            ))}
          </div>

          {/* While exporting: mount an off-screen QR canvas per team so exportAllPdf
              can read each as a PNG for the jsPDF pages. Off-screen is fine here —
              toDataURL is unaffected by position (unlike html2canvas text). An
              opaque overlay shows progress. */}
          {exporting ? (
            <>
              <div aria-hidden style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
                {withCodeTeams.map((t) => (
                  <span key={t.id} data-eqr={t.id}>
                    <QRCodeCanvas value={linkOf(t)} size={300} marginSize={2} fgColor="#000000" bgColor="#FFFFFF" />
                  </span>
                ))}
              </div>
              <div
                className="no-print"
                style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(18,61,38,0.97)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: 24 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Generating team-card PDF…</div>
                <div style={{ fontSize: 15 }}>{exportProgress ? `${exportProgress.done} / ${exportProgress.total} cards` : 'Preparing…'}</div>
                <div style={{ fontSize: 12, opacity: 0.85, maxWidth: 340 }}>Almost done…</div>
              </div>
            </>
          ) : null}
        </>)}
      </div>
    </div>
  )
}

export function QualifyingEventDetailPage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [showCreateChoice, setShowCreateChoice] = useState(false)
  // Submit-to-Tournament opens the AI-vs-Manual choice modal (same two buttons
  // the New-Tournament promote flow uses) instead of silently building. The
  // modal owns its own busy state, so no local `building` flag is needed.
  const [showSubmitChoice, setShowSubmitChoice] = useState(false)
  // Delete access-code gate (in-app modal, not window.prompt).
  const [deleteGate, setDeleteGate] = useState(false)
  const [delCode, setDelCode] = useState('')
  const [delErr, setDelErr] = useState('')
  const [approveGate, setApproveGate] = useState(false)
  const [apprCode, setApprCode] = useState('')
  const [apprErr, setApprErr] = useState('')
  // Bottom "Teams" section is tabbed: Qualified | Not Qualified | Team QR Codes.
  // Each tab shows only its own list; the two team tabs share one search box
  // that clears on tab switch so each tab searches fresh.
  const [activeTab, setActiveTab] = useState('qualified') // 'qualified' | 'notqualified' | 'qr'
  const [teamSearch, setTeamSearch] = useState('')
  // Print the clean leaderboard sheet (reference-style). Reset after printing.
  const [lbPrint, setLbPrint] = useState(false)
  // Import GG PDF — upload a Golf Genius leaderboard PDF, preview, then add the
  // missing qualified teams (dedup by last names; existing teams untouched).
  const ggFileRef = useRef(null)
  const [ggBusy, setGgBusy] = useState('') // '' or a status line while working
  const [ggPreview, setGgPreview] = useState(null) // { adding, skipping, flagged } | null
  const [ggError, setGgError] = useState('')

  useEffect(() => {
    const reset = () => setLbPrint(false)
    window.addEventListener('afterprint', reset)
    return () => window.removeEventListener('afterprint', reset)
  }, [])

  useEffect(() => {
    if (!eventId) return
    const off = watchQualifyingEvent(eventId, (v) => {
      setEvent(v)
      setLoaded(true)
    })
    return () => off()
  }, [eventId])

  if (!loaded) {
    return (
      <DetailShell>
        <p className="text-sm text-gray-600">Loading…</p>
      </DetailShell>
    )
  }
  if (!event) {
    return (
      <DetailShell>
        <p className="text-sm" style={{ color: '#B91C1C' }}>
          Qualifying event not found.
        </p>
        <Link to="/admin/pbcc-4ball" className="btn-primary mt-3 inline-block">← PBCC 4-Ball</Link>
      </DetailShell>
    )
  }

  const info = event.info || {}
  const teamsMap = event.teams || {}
  const teams = Object.entries(teamsMap).map(([id, t]) => ({ id, ...(t || {}) }))
  // A team is QUALIFIED when it has a total SCORE — nothing else. Whether the
  // hole-by-hole scorecard was filled in (submittedAt) is the admin's choice and
  // must NOT affect qualified status.
  const hasTotalScore = (t) => t?.score != null && Number.isFinite(Number(t.score)) && Number(t.score) > 0
  // Sort: scored lowest-score first, unscored teams to the bottom.
  teams.sort((a, b) => {
    const ap = hasTotalScore(a)
    const bp = hasTotalScore(b)
    if (ap !== bp) return ap ? -1 : 1
    if (ap && bp) return (a.score || 0) - (b.score || 0)
    return (a.createdAt || 0) - (b.createdAt || 0)
  })

  const closed = info.status === 'closed'
  // `origin` still feeds the Team QR Codes tab (per-team play links).
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const submittedCount = teams.filter((t) => !!t.submittedAt).length
  // Split for the tabbed Teams section — keyed off HAS-A-SCORE, so a scored team
  // never lands in Not Qualified and no team can appear in both tabs (the two
  // filters are exact complements). Both keep the parent `teams` sort order.
  const qualifiedTeams = teams.filter(hasTotalScore)
  const notQualifiedTeams = teams.filter((t) => !hasTotalScore(t))
  const matchesTeam = (t) => {
    const q = teamSearch.trim().toLowerCase()
    if (!q) return true
    return `${t.teamName || ''} ${t.player1 || ''} ${t.player2 || ''}`.toLowerCase().includes(q)
  }
  const shownQualified = qualifiedTeams.filter(matchesTeam)
  const shownNotQualified = notQualifiedTeams.filter(matchesTeam)
  const switchTab = (tab) => { setActiveTab(tab); setTeamSearch('') }

  // Import GG PDF: read the file → parseGgPdf (server pulls + verifies every
  // scorecard) → categorize against the CURRENT field → show the preview.
  const onGgFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // let the admin re-pick the same file later
    if (!file) return
    setGgError(''); setGgPreview(null); setGgBusy('Reading PDF…')
    try {
      const pdfBase64 = await new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result).split(',')[1] || '')
        r.onerror = () => reject(new Error('Could not read that file.'))
        r.readAsDataURL(file)
      })
      setGgBusy('Parsing leaderboard + pulling scorecards…')
      const call = httpsCallable(getFunctions(app, 'us-central1'), 'parseGgPdf')
      const { data } = await call({ pdfBase64 })
      // Mirror the FULL PDF field: scored teams + NS roster → add / update / skip /
      // flag / REMOVE strays. nsTeams lets removal keep on-PDF NS teams.
      const cat = categorizeImport(data?.teams || [], teamsMap, data?.nsTeams || [])
      setGgPreview(cat)
    } catch (err) {
      setGgError(err?.message || 'Import failed — please try again.')
    } finally {
      setGgBusy('')
    }
  }
  // Approve: make the field an EXACT MIRROR of the PDF — ADD missing teams,
  // UPDATE any whose score disagreed with the PDF (score + hole-by-hole), and
  // REMOVE strays not on the PDF. Teams that already match are untouched. The
  // live listener refreshes the tabs on write.
  const onGgApprove = async () => {
    const nAdd = ggPreview?.adding?.length || 0
    const nUpd = ggPreview?.updating?.length || 0
    const nRem = ggPreview?.removing?.length || 0
    if (nAdd + nUpd + nRem === 0) { setGgPreview(null); return }
    setGgBusy('Applying changes…')
    try {
      await applyQualifyingImport(eventId, { adding: ggPreview.adding, updating: ggPreview.updating, removing: ggPreview.removing })
      setGgPreview(null)
    } catch (err) {
      setGgError(err?.message || 'Could not apply the changes.')
    } finally {
      setGgBusy('')
    }
  }
  // Not Qualified list — NAMES ONLY. These teams have no score, nothing to
  // approve, and no scorecard, so it's a clean Team & Players list (Team-QR-tab
  // row style). The Qualified tab keeps its full leaderboard (qualifiedTable).
  const teamTable = (list, emptyMsg) => (
    list.length === 0 ? (
      <p className="text-sm text-gray-600 italic px-1 py-2">{emptyMsg}</p>
    ) : (
      <ul className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${ACCENT}55` }}>
        {list.map((t) => (
          <li key={t.id} className="px-3 py-2.5 border-t first:border-t-0"
              style={{ borderColor: 'rgba(18, 61, 38,0.20)', background: '#FFFFFF' }}>
            <span className="font-bold" style={{ color: PRIMARY }}>{t.teamName}</span>
            {(t.player1 || t.player2) ? (
              <span className="text-sm text-gray-500 ml-2">{[t.player1, t.player2].filter(Boolean).join(' & ')}</span>
            ) : null}
          </li>
        ))}
      </ul>
    )
  )

  // ── Qualified = ranked leaderboard ────────────────────────────────────────
  // Lowest Total Gross = Position 1, ascending (qualifiedTeams is already
  // score-sorted). Ties sit next to each other with sequential positions.
  // Columns: Position | Players | To Par Gross | Total Gross. Par is the host
  // course par (PBCC = 71) from the shared scoring lib.
  const PAR = totalPar()
  // True field position per team (computed on the full, unfiltered qualified
  // field so a search box never renumbers the leaderboard).
  const positionById = new Map(qualifiedTeams.map((t, i) => [t.id, i + 1]))
  const fmtToPar = (score) => {
    const d = (Number(score) || 0) - PAR
    return d === 0 ? 'E' : (d > 0 ? `+${d}` : String(d))
  }
  // Print the clean reference-style leaderboard (see the hidden .qlb-sheet).
  const printLeaderboard = () => { setLbPrint(true); setTimeout(() => { try { window.print() } catch { /* ignore */ } }, 120) }

  // Downloadable ranked-leaderboard PDF — fully SELF-CONTAINED (no outbound
  // links). Clicking a team name jumps to that team's hole-by-hole scorecard
  // page later in the SAME PDF; each scorecard page links back to the
  // leaderboard. Nothing opens the site. Uses the real stored per-team holes[]
  // + score. Total-only teams show their total.
  const downloadLeaderboardPdf = async () => {
    if (!qualifiedTeams.length) return
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ unit: 'pt', format: 'letter' })
    const PW = doc.internal.pageSize.getWidth()
    const PH = doc.internal.pageSize.getHeight()
    const M = 42
    const BLACK = '#111827'
    const LINK = '#1D4ED8'
    const pars = DEFAULT_PARS
    const holeVal = (t, h) => {
      const src = t?.holes || {}
      const raw = src[h] ?? src[String(h)]
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    const hasHbh = (t) => { for (let h = 1; h <= 18; h++) if (holeVal(t, h) != null) return true; return false }

    // ── Leaderboard pages ── draw the ranked table; record each name's box so
    // we can attach the internal jump link once scorecard pages exist.
    const nameBoxes = [] // { page, x, y, w, h }
    let y = M
    doc.setTextColor(BLACK)
    const drawLbHeader = () => {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(15)
      const title = String(info.name || 'Qualifying Event').split('\n')
      title.forEach((ln) => { doc.text(ln, PW / 2, y, { align: 'center' }); y += 17 })
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5)
      if (eventDateStr) { doc.text(eventDateStr, PW / 2, y, { align: 'center' }); y += 14 }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5)
      doc.text('QUALIFYING STROKE PLAY LEADERBOARD', PW / 2, y + 2, { align: 'center' }); y += 22
      // column header
      doc.setFontSize(9)
      doc.text('POS', M, y)
      doc.text('PLAYERS', M + 44, y)
      doc.text('TO PAR GROSS', PW - M - 150, y, { align: 'right' })
      doc.text('TOTAL GROSS', PW - M, y, { align: 'right' })
      y += 6
      doc.setDrawColor(0); doc.setLineWidth(1.2); doc.line(M, y, PW - M, y); y += 14
    }
    drawLbHeader()
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5)
    qualifiedTeams.forEach((t, i) => {
      if (y > PH - M - 20) { doc.addPage(); y = M; drawLbHeader(); doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5) }
      const pos = i + 1
      const toPar = fmtToPar(t.score)
      const names = [t.player1, t.player2].filter(Boolean).join(' / ')
      const nameStr = names || '—'
      doc.setTextColor(BLACK)
      doc.text(String(pos), M, y)
      // team name — record its box for the internal link (added later)
      const nx = M + 44
      const nw = doc.getTextWidth(nameStr)
      doc.text(nameStr, nx, y)
      nameBoxes.push({ page: doc.getCurrentPageInfo().pageNumber, x: nx, y: y - 9, w: nw, h: 12 })
      doc.text(toPar, PW - M - 150, y, { align: 'right' })
      doc.setFont('helvetica', 'bold')
      doc.text(String(t.score), PW - M, y, { align: 'right' })
      doc.setFont('helvetica', 'normal')
      y += 18
    })

    // ── One scorecard page per team ── record each page number.
    const drawGrid = (t, gy) => {
      const cols = 11 // label + 9 holes + OUT/IN
      const cw = (PW - 2 * M) / cols
      const rowH = 20
      const cell = (cx, ry, txt, opt = {}) => {
        doc.setDrawColor(180); doc.setLineWidth(0.5)
        doc.rect(cx, ry, cw, rowH)
        doc.setFont('helvetica', opt.bold ? 'bold' : 'normal'); doc.setFontSize(9.5)
        if (txt !== '' && txt != null) doc.text(String(txt), cx + cw / 2, ry + 13, { align: 'center' })
      }
      const nine = (start, label, ry) => {
        // header row: Hole 1..9 + OUT
        cell(M, ry, 'Hole', { bold: true })
        for (let k = 0; k < 9; k++) cell(M + cw * (k + 1), ry, start + k, { bold: true })
        cell(M + cw * 10, ry, label, { bold: true })
        // par row
        cell(M, ry + rowH, 'Par')
        let pSum = 0
        for (let k = 0; k < 9; k++) { const p = pars[start - 1 + k] || 4; pSum += p; cell(M + cw * (k + 1), ry + rowH, p) }
        cell(M + cw * 10, ry + rowH, pSum, { bold: true })
        // score row
        cell(M, ry + rowH * 2, 'Score', { bold: true })
        let sSum = 0; let any = false
        for (let k = 0; k < 9; k++) { const v = holeVal(t, start + k); if (v != null) { sSum += v; any = true }; cell(M + cw * (k + 1), ry + rowH * 2, v == null ? '–' : v) }
        cell(M + cw * 10, ry + rowH * 2, any ? sSum : '–', { bold: true })
        return ry + rowH * 3
      }
      let ry = gy
      ry = nine(1, 'OUT', ry) + 10
      ry = nine(10, 'IN', ry) + 10
      return ry
    }
    const scorecardPageForTeam = []
    qualifiedTeams.forEach((t, i) => {
      doc.addPage()
      scorecardPageForTeam[i] = doc.getCurrentPageInfo().pageNumber
      let cy = M
      doc.setTextColor(BLACK); doc.setFont('helvetica', 'bold'); doc.setFontSize(15)
      const names = [t.player1, t.player2].filter(Boolean).join(' / ')
      doc.text(names || '—', M, cy); cy += 20
      doc.setFont('helvetica', 'normal'); doc.setFontSize(11)
      doc.text(`Position ${i + 1}  ·  To Par Gross ${fmtToPar(t.score)}  ·  Total Gross ${t.score}`, M, cy); cy += 8
      // back-to-leaderboard INTERNAL link (stays inside the PDF)
      doc.setFontSize(9.5); doc.setTextColor(LINK)
      doc.textWithLink('← Back to leaderboard', PW - M - 130, M, { pageNumber: 1 })
      doc.setTextColor(BLACK)
      cy += 16
      if (hasHbh(t)) {
        drawGrid(t, cy)
      } else {
        doc.setFont('helvetica', 'italic'); doc.setFontSize(12)
        doc.text(`Hole-by-hole scorecard not recorded — Total score: ${t.score}`, M, cy + 10)
        doc.setFont('helvetica', 'normal')
      }
    })

    // ── Attach the INTERNAL jump links over each leaderboard name box ──
    nameBoxes.forEach((b, i) => {
      doc.setPage(b.page)
      doc.link(b.x, b.y, b.w, b.h, { pageNumber: scorecardPageForTeam[i] })
    })

    const safe = String(info.name || 'leaderboard').split('\n')[0].replace(/[^a-z0-9]+/gi, '_').slice(0, 40)
    doc.save(`${safe}_leaderboard.pdf`)
  }
  const eventDateStr = (() => {
    const d = info.date
    if (!d) return ''
    const dt = new Date(`${d}T00:00:00`)
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  })()
  const qualifiedTable = (fullList, list, emptyMsg) => (
    list.length === 0 ? (
      <p className="text-sm text-gray-600 italic px-1 py-2">{emptyMsg}</p>
    ) : (
      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${ACCENT}55` }}>
        <div className="grid grid-cols-12 gap-1 px-3 py-2 text-sm uppercase tracking-widest font-extrabold"
             style={{ background: '#FFFFFF', color: '#111827' }}>
          <div className="col-span-1">Pos</div>
          <div className="col-span-5">Players</div>
          <div className="col-span-2 text-center">Scorecard</div>
          <div className="col-span-1 text-center">Approved</div>
          <div className="col-span-1 text-right">To Par</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        {list.map((t) => (
          <QualifiedRow key={t.id} eventId={eventId} team={t} closed={closed}
            position={positionById.get(t.id)} toPar={fmtToPar(t.score)} />
        ))}
      </div>
    )
  )

  // Approve & Lock: approve the WHOLE field (digital or paper) AND close the
  // round in one action, so an approved field is a locked field — the signal
  // New Tournament reads as "ready to build." Never gated on digital submissions.
  const onApproveAll = async () => {
    setBusy(true); setErr('')
    try {
      await approveAllTeams(eventId, teamsMap)
      await setQualifyingEventStatus(eventId, 'closed')
    }
    catch (e) { setErr(e?.message || 'Approve failed.') }
    finally { setBusy(false) }
  }

  const onToggleLock = async () => {
    try { await setQualifyingEventLocked(eventId, !info.locked) }
    catch (e) { setErr(e?.message || 'Lock toggle failed.') }
  }
  // Reset Approvals — drops approved:false and status:'submitted' on
  // every team so the admin can re-run the approve flow from scratch.
  // Useful during testing and when an approval was made in error.
  const onResetApprovals = async () => {
    if (!window.confirm('Reset every team back to "submitted" status? Approvals will be cleared.')) return
    setBusy(true); setErr('')
    try {
      const updates = {}
      for (const teamId of Object.keys(teamsMap || {})) {
        updates[`qualifyingEvents/${eventId}/teams/${teamId}/approved`] = false
        updates[`qualifyingEvents/${eventId}/teams/${teamId}/status`] = 'submitted'
      }
      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates)
      }
    } catch (e) {
      setErr(e?.message || 'Reset failed.')
    } finally {
      setBusy(false)
    }
  }
  const onClose = async () => {
    if (!window.confirm('Close qualifying? Players will no longer be able to submit scores.')) return
    setBusy(true); setErr('')
    try { await setQualifyingEventStatus(eventId, 'closed') }
    catch (e) { setErr(e?.message || 'Close failed.') }
    finally { setBusy(false) }
  }
  const onReopen = async () => {
    setBusy(true); setErr('')
    try { await setQualifyingEventStatus(eventId, 'open') }
    catch (e) { setErr(e?.message || 'Reopen failed.') }
    finally { setBusy(false) }
  }
  // Access-code-gated delete. The 4-digit code (1996) is entered in an IN-APP
  // modal (openDeleteGate → submitDeleteCode), NOT window.prompt — native
  // prompts can be suppressed by the browser and were easy to miss. Guards the
  // real 104-team qualifying event from an accidental delete: doDelete only runs
  // after the code checks out; Cancel or a wrong code deletes nothing.
  const openDeleteGate = () => { setDelCode(''); setDelErr(''); setDeleteGate(true) }
  const submitDeleteCode = () => {
    if (delCode.trim() !== '1996') { setDelErr('Incorrect access code — deletion cancelled.'); return }
    setDeleteGate(false)
    doDelete()
  }
  // Approve & Lock is gated by the SAME 4-digit code as delete — an in-app modal so
  // it can't be triggered by an accidental tap. onApproveAll only runs once the code
  // checks out; Cancel or a wrong code approves nothing.
  const openApproveGate = () => { setApprCode(''); setApprErr(''); setApproveGate(true) }
  const submitApproveCode = () => {
    if (apprCode.trim() !== '1996') { setApprErr('Incorrect access code — approval cancelled.'); return }
    setApproveGate(false)
    onApproveAll()
  }
  const doDelete = async () => {
    setBusy(true); setErr('')
    try {
      await deleteQualifyingEvent(eventId)
      navigate('/admin')
    } catch (e) {
      setErr(e?.message || 'Delete failed.')
      setBusy(false)
    }
  }

  return (
    <DetailShell>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm uppercase tracking-widest font-extrabold" style={{ color: ACCENT }}>
            Qualifying Event
          </p>
          {(() => {
            const { nameLines, dateLine } = splitEventTitle(info)
            return (
              <>
                <h1 className="text-2xl font-extrabold" style={{ color: PRIMARY, lineHeight: 1.2 }}>
                  {(nameLines.length ? nameLines : [info.name || 'Untitled']).map((l, i) => <div key={i}>{l}</div>)}
                </h1>
                {dateLine ? <p className="text-sm font-semibold mt-0.5" style={{ color: ACCENT }}>{dateLine}</p> : null}
              </>
            )
          })()}
          <p className="text-sm text-gray-600 mt-0.5">
            {info.format} · Created {info.createdAt ? new Date(info.createdAt).toLocaleDateString() : '—'}
          </p>
        </div>
        <StatusBadge status={info.status} />
      </div>



      {/* Admin actions row */}
      <div className="mt-4 flex flex-wrap gap-2">
        {!closed ? (
          <>
            <button
              type="button"
              onClick={openApproveGate}
              disabled={busy}
              className="btn-primary text-sm">
              ✅ Approve &amp; Lock Field
            </button>
            <button
              type="button"
              onClick={onResetApprovals}
              disabled={busy || teams.length === 0}
              className="btn-secondary text-sm">
              ↺ Reset Approvals
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn-secondary text-sm">
              🔒 Close Qualifying
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onReopen}
              disabled={busy}
              className="btn-secondary text-sm">
              🔓 Re-open Qualifying
            </button>
            {info.sourceRegistrationId ? (
              <button
                type="button"
                onClick={() => setShowSubmitChoice(true)}
                disabled={busy}
                className="btn-primary text-sm">
                🏆 Submit to Tournament
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowCreateChoice(true)}
                className="btn-primary text-sm">
                ▶ Create Tournament from This List
              </button>
            )}
          </>
        )}
        <button
          type="button"
          onClick={onToggleLock}
          disabled={busy}
          className="btn-secondary text-sm"
          style={{ marginLeft: 'auto' }}
          title={info.locked ? 'Locked — delete needs the access code' : 'Lock to guard against accidental delete'}>
          {info.locked ? '🔒 Locked' : '🔓 Lock'}
        </button>
        <button
          type="button"
          onClick={openDeleteGate}
          disabled={busy}
          className="btn-ghost text-sm"
          style={{ color: '#B91C1C' }}>
          🗑 Delete Event{info.locked ? ' 🔒' : ''}
        </button>
      </div>
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}

      {/* Import GG PDF — bulk-add qualified teams from a Golf Genius leaderboard
          PDF. The server pulls + verifies each embedded scorecard; duplicates
          (same last-name pair) are skipped, existing teams never touched. */}
      <div className="mt-5 rounded-xl p-3" style={{ background: '#FFFFFF', border: `1px solid ${ACCENT}33` }}>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={ggFileRef} type="file" accept="application/pdf" className="hidden" onChange={onGgFile} />
          <button
            type="button"
            disabled={!!ggBusy || closed}
            onClick={() => ggFileRef.current?.click()}
            className="rounded-lg px-3.5 py-2 text-sm font-extrabold transition active:scale-[0.98]"
            style={{ background: PRIMARY, color: '#FFFFFF', opacity: ggBusy || closed ? 0.6 : 1 }}>
            📥 Import GG PDF
          </button>
          {ggBusy ? <span className="text-sm text-gray-600">{ggBusy}</span> : null}
          {ggError ? <span className="text-sm font-bold" style={{ color: '#B91C1C' }}>{ggError}</span> : null}
        </div>
        <p className="text-sm text-gray-500 mt-1.5">
          Upload the club’s Golf Genius leaderboard PDF — the field becomes an <b>exact mirror</b> of it: adds missing teams
          (with hole-by-hole), updates any whose score differs, and <b>removes teams not on the PDF</b>. After import the count
          equals the PDF exactly. {closed ? 'Reopen the event to import.' : 'You’ll preview every add / update / remove before anything is written.'}
        </p>
      </div>

      {/* Preview modal — ADDING / SKIPPING / FLAGGED, admin approves before write */}
      {ggPreview ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-extrabold" style={{ color: PRIMARY }}>Import GG PDF — Preview (mirror)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Mirroring the PDF{ggPreview.pdfTotal != null ? <> (<b>{ggPreview.pdfTotal}</b> teams)</> : null} — after import the field will hold exactly {ggPreview.pdfTotal != null ? <b>{ggPreview.pdfTotal}</b> : 'the PDF’s'} teams.
            </p>
            <p className="text-sm text-gray-600 mt-1">
              <b className="text-green-700">{ggPreview.adding.length} to add</b>
              {(ggPreview.updating?.length || 0) > 0 ? <> · <b style={{ color: '#1D4ED8' }}>{ggPreview.updating.length} to update</b></> : null}
              {(ggPreview.removing?.length || 0) > 0 ? <> · <b style={{ color: '#B91C1C' }}>{ggPreview.removing.length} to remove</b></> : null}
              {' '}· {ggPreview.skipping.length} already match (skip)
              {ggPreview.flagged.length > 0 ? <> · <b style={{ color: '#B45309' }}>{ggPreview.flagged.length} flagged</b></> : null}
            </p>

            <div className="mt-3">
              <div className="text-sm font-extrabold text-green-700 mb-1">ADDING ({ggPreview.adding.length})</div>
              {ggPreview.adding.length === 0 ? (
                <p className="text-sm text-gray-500">Nothing new to add.</p>
              ) : (
                ggPreview.adding.map((t, i) => (
                  <div key={i} className="text-sm border-b border-gray-100 py-1.5">
                    <div><b>{t.teamName}</b> — {t.player1} / {t.player2} — Total <b>{t.total}</b></div>
                    <div className="text-gray-500 font-mono mt-0.5">
                      {t.holes.slice(1, 10).join(' ')} = {t.out}&nbsp;&nbsp;|&nbsp;&nbsp;{t.holes.slice(10, 19).join(' ')} = {t.in}&nbsp;&nbsp;|&nbsp;&nbsp;T {t.total}
                    </div>
                  </div>
                ))
              )}
            </div>

            {(ggPreview.updating?.length || 0) > 0 ? (
              <div className="mt-3">
                <div className="text-sm font-extrabold mb-1" style={{ color: '#1D4ED8' }}>UPDATING to match PDF ({ggPreview.updating.length})</div>
                {ggPreview.updating.map((t, i) => (
                  <div key={i} className="text-sm border-b border-gray-100 py-1.5">
                    <div><b>{t.player1} / {t.player2}</b> — was <b>{t.oldScore}</b> → PDF <b style={{ color: '#1D4ED8' }}>{t.newScore}</b></div>
                    <div className="text-gray-500 font-mono mt-0.5">
                      {t.holes.slice(1, 10).join(' ')} = {t.out}&nbsp;&nbsp;|&nbsp;&nbsp;{t.holes.slice(10, 19).join(' ')} = {t.in}&nbsp;&nbsp;|&nbsp;&nbsp;T {t.total}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {(ggPreview.removing?.length || 0) > 0 ? (
              <div className="mt-3 rounded-lg p-2" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
                <div className="text-sm font-extrabold mb-1" style={{ color: '#B91C1C' }}>REMOVING — not on the PDF ({ggPreview.removing.length})</div>
                {ggPreview.removing.map((t, i) => (
                  <div key={i} className="text-sm border-b py-1" style={{ borderColor: '#FECACA22', color: '#B91C1C' }}>
                    <b>{t.teamName || `${t.player1} / ${t.player2}`}</b> — {t.player1} / {t.player2}{t.score != null ? <> — score {t.score}</> : <> — no score</>}
                  </div>
                ))}
                <div className="text-xs mt-1" style={{ color: '#B91C1C' }}>These teams will be DELETED from the event so the field matches the PDF exactly.</div>
              </div>
            ) : null}

            {ggPreview.skipping.length > 0 ? (
              <details className="mt-3">
                <summary className="text-sm font-extrabold text-gray-700 cursor-pointer">SKIPPING — already match ({ggPreview.skipping.length})</summary>
                <div className="mt-1">
                  {ggPreview.skipping.map((t, i) => (
                    <div key={i} className="text-sm text-gray-600 py-0.5">{t.player1} / {t.player2} ({t.total})</div>
                  ))}
                </div>
              </details>
            ) : null}

            {ggPreview.flagged.length > 0 ? (
              <div className="mt-3">
                <div className="text-sm font-extrabold mb-1" style={{ color: '#B45309' }}>FLAGGED — not added ({ggPreview.flagged.length})</div>
                {ggPreview.flagged.map((t, i) => (
                  <div key={i} className="text-sm py-0.5" style={{ color: '#B45309' }}>{t.player1} / {t.player2}: {t.reason}</div>
                ))}
              </div>
            ) : null}

            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" className="btn-ghost text-sm" onClick={() => setGgPreview(null)} disabled={!!ggBusy}>Cancel</button>
              {(() => {
                const nAdd = ggPreview.adding.length
                const nUpd = ggPreview.updating?.length || 0
                const nRem = ggPreview.removing?.length || 0
                const parts = []
                if (nAdd) parts.push(`Add ${nAdd}`)
                if (nUpd) parts.push(`Update ${nUpd}`)
                if (nRem) parts.push(`Remove ${nRem}`)
                const label = parts.length ? parts.join(' · ') : 'Nothing to apply'
                return (
                  <button type="button" className="btn-primary text-sm" onClick={onGgApprove} disabled={!!ggBusy || nAdd + nUpd + nRem === 0}>
                    {ggBusy || label}
                  </button>
                )
              })()}
            </div>
          </div>
        </div>
      ) : null}

      {/* Teams section — tabbed into Qualified / Not Qualified / Team QR Codes
          (replaces the old single long-scroll list). Each tab shows only its
          own list; the two team tabs have their own search box. The top of the
          page (both QR boxes + action buttons) is unchanged above this. */}
      <div className="mt-5">
        {/* Tab bar */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[
            { key: 'qualified', label: `Qualified (${qualifiedTeams.length})` },
            { key: 'notqualified', label: `Not Qualified (${notQualifiedTeams.length})` },
            { key: 'qr', label: `Team QR Codes (${teams.length})` },
          ].map((tab) => {
            const on = activeTab === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => switchTab(tab.key)}
                className="rounded-lg px-3.5 py-2 text-sm font-extrabold transition active:scale-[0.98]"
                style={on
                  ? { background: PRIMARY, color: '#FFFFFF' }
                  : { background: '#FFFFFF', color: PRIMARY, border: `1px solid ${ACCENT}55` }}>
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Qualified / Not Qualified tabs — search box + their own list */}
        {activeTab !== 'qr' ? (
          teams.length === 0 ? (
            <p className="text-sm text-gray-600 italic">
              No teams yet. Share the QR — players check in and start their qualifying round from there.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <input
                  type="text"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Search teams by name…"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-72" />
                {activeTab === 'qualified' && qualifiedTeams.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={printLeaderboard}
                      className="btn-primary text-sm px-3 py-2"
                      style={{ backgroundColor: PRIMARY }}
                      title="Print / save the clean leaderboard as PDF">
                      🖨 Print Leaderboard
                    </button>
                    <button
                      type="button"
                      onClick={downloadLeaderboardPdf}
                      className="btn-primary text-sm px-3 py-2"
                      style={{ backgroundColor: PRIMARY }}
                      title="Download a self-contained PDF: ranked leaderboard + a scorecard page per team. Each name jumps to its scorecard inside the PDF (no outside links).">
                      ⬇ Download PDF
                    </button>
                  </>
                ) : null}
              </div>
              {activeTab === 'qualified'
                ? qualifiedTable(qualifiedTeams, shownQualified, teamSearch.trim() ? `No qualified team matches “${teamSearch}”.` : 'No qualified teams yet.')
                : teamTable(shownNotQualified, teamSearch.trim() ? `No unqualified team matches “${teamSearch}”.` : 'Every team has submitted a qualifying score.')}
            </>
          )
        ) : null}

        {/* Team QR Codes tab */}
        {activeTab === 'qr' ? (
          PBCC_PREMIUM
            ? <TeamQrCodesSection eventId={eventId} origin={origin} teams={teams} teamsMap={teamsMap} eventName={info.name} />
            : <p className="text-sm text-gray-600 italic">Team QR codes aren’t available on this site.</p>
        ) : null}
      </div>

      {showCreateChoice ? (
        <CreateTournamentChoiceModal
          eventId={eventId}
          info={info}
          submittedCount={submittedCount}
          teamsMap={teamsMap}
          onClose={() => setShowCreateChoice(false)}
        />
      ) : null}

      {/* Submit to Tournament → the field list + two build paths (AI / Manual).
          Both run the SAME deterministic build (onBuild = buildTournamentFromQualifying);
          "Build with AI" then opens the AI Assistant primed, "Build Manually" drops
          straight into the tournament admin. */}
      {showSubmitChoice ? (
        <TournamentCreatedRosterModal
          tournamentId={info.sourceRegistrationId}
          approvedTeams={teams
            .filter((t) => t.approved)
            .map((t) => ({ teamName: t.teamName, qualifyingScore: t.score, player1: t.player1, player2: t.player2 }))}
          tournamentName={info.name || ''}
          qualifyingEventName={info.name || ''}
          titleText="Submit to Tournament — Ready to Build"
          introText={<>Your qualifying field is approved and locked. Pick how to build the tournament from these teams.</>}
          onBuild={async () => {
            await buildTournamentFromQualifying(info.sourceRegistrationId, eventId, { isPractice: info.isPractice })
          }}
          onClose={() => setShowSubmitChoice(false)}
        />
      ) : null}

      {/* Delete access-code gate — in-app modal (not window.prompt). Deleting a
          qualifying event requires the 4-digit code 1996; Cancel or a wrong code
          deletes nothing. Protects the real 104-team event from a fat-finger. */}
      {deleteGate ? (
        <div
          onClick={() => setDeleteGate(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 20 }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: '#B91C1C' }}>Delete qualifying event?</h3>
            <p style={{ fontSize: 13, color: '#374151', margin: '8px 0 14px', lineHeight: 1.45 }}>
              This permanently deletes the entire event and cannot be undone. Enter the 4-digit access code to confirm.
            </p>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={delCode}
              onChange={(e) => { setDelCode(e.target.value.replace(/\D/g, '')); setDelErr('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitDeleteCode() }}
              placeholder="• • • •"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 24, letterSpacing: 8, textAlign: 'center', padding: '10px 12px', borderRadius: 10, border: `2px solid ${delErr ? '#B91C1C' : '#D1D5DB'}`, outline: 'none' }}
            />
            {delErr ? <p style={{ color: '#B91C1C', fontSize: 12, fontWeight: 800, margin: '8px 0 0' }}>{delErr}</p> : null}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setDeleteGate(false)} className="btn-secondary text-sm" style={{ flex: 1 }}>Cancel</button>
              <button
                type="button"
                onClick={submitDeleteCode}
                disabled={delCode.length < 4}
                className="btn-primary text-sm"
                style={{ flex: 1, background: '#B91C1C', borderColor: '#B91C1C', opacity: delCode.length < 4 ? 0.5 : 1 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Approve & Lock access-code gate — SAME 4-digit code (1996) as delete, so the
          field can't be approved/locked by an accidental tap. onApproveAll runs only
          after the code checks out; Cancel or a wrong code approves nothing. */}
      {approveGate ? (
        <div
          onClick={() => setApproveGate(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 20 }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: PRIMARY }}>Approve &amp; Lock the field?</h3>
            <p style={{ fontSize: 13, color: '#374151', margin: '8px 0 14px', lineHeight: 1.45 }}>
              This approves the whole field and closes qualifying — it can then be built into a tournament. Enter the 4-digit access code to confirm.
            </p>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={apprCode}
              onChange={(e) => { setApprCode(e.target.value.replace(/\D/g, '')); setApprErr('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitApproveCode() }}
              placeholder="• • • •"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 24, letterSpacing: 8, textAlign: 'center', padding: '10px 12px', borderRadius: 10, border: `2px solid ${apprErr ? '#B91C1C' : '#D1D5DB'}`, outline: 'none' }}
            />
            {apprErr ? <p style={{ color: '#B91C1C', fontSize: 12, fontWeight: 800, margin: '8px 0 0' }}>{apprErr}</p> : null}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setApproveGate(false)} className="btn-secondary text-sm" style={{ flex: 1 }}>Cancel</button>
              <button
                type="button"
                onClick={submitApproveCode}
                disabled={apprCode.length < 4}
                className="btn-primary text-sm"
                style={{ flex: 1, opacity: apprCode.length < 4 ? 0.5 : 1 }}>
                Approve &amp; Lock
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Clean printable leaderboard (reference style). Hidden on screen; when
          Print Leaderboard is tapped, @media print hides everything else and
          shows only this sheet — plain black, no controls/badges/chips. */}
      {lbPrint ? (
        <style>{`@media print {
          body * { visibility: hidden !important; }
          .qlb-sheet, .qlb-sheet * { visibility: visible !important; }
          .qlb-sheet { position: absolute; left: 0; top: 0; width: 100%; }
          @page { margin: 14mm; }
        }`}</style>
      ) : null}
      <div className="qlb-sheet" style={{ display: lbPrint ? 'block' : 'none', color: '#000', background: '#fff', padding: '8px 6px', fontFamily: 'Georgia, "Times New Roman", serif' }}>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25, whiteSpace: 'pre-line' }}>{info.name || 'Qualifying Event'}</div>
          {eventDateStr ? <div style={{ fontSize: 13, marginTop: 4 }}>{eventDateStr}</div> : null}
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Qualifying Stroke Play Leaderboard</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #000' }}>
              <th style={{ textAlign: 'left', padding: '5px 6px', width: '10%' }}>Position</th>
              <th style={{ textAlign: 'left', padding: '5px 6px', width: '58%' }}>Players</th>
              <th style={{ textAlign: 'right', padding: '5px 6px', width: '16%' }}>To Par Gross</th>
              <th style={{ textAlign: 'right', padding: '5px 6px', width: '16%' }}>Total Gross</th>
            </tr>
          </thead>
          <tbody>
            {qualifiedTeams.map((t, i) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #ccc' }}>
                <td style={{ textAlign: 'left', padding: '4px 6px' }}>{i + 1}</td>
                <td style={{ textAlign: 'left', padding: '4px 6px' }}>
                  #{t.teamName} {[t.player1, t.player2].filter(Boolean).join(' / ')}
                </td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmtToPar(t.score)}</td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontVariantNumeric: 'tabular-nums' }}>{t.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DetailShell>
  )
}

// Leaderboard row for the Qualified tab: Position | Players | To Par | Total.
// Keeps the team number and both player names, plus the View Scorecard expander
// and the per-row Approve control (admin function retained). Ranked by score.
function QualifiedRow({ eventId, team, closed, position, toPar }) {
  const [expanded, setExpanded] = useState(false)
  const hasScore = team?.score != null && Number.isFinite(Number(team.score)) && Number(team.score) > 0
  const holeByHole = team?.holes || {}
  const hasHoleByHole = (() => { for (let h = 1; h <= 18; h++) { const n = Number(holeByHole[h]); if (Number.isFinite(n) && n > 0) return true } return false })()
  const totalOnly = hasScore && !hasHoleByHole
  const onToggleApproved = async (e) => {
    const next = e.target.checked
    const { update, ref } = await import('firebase/database')
    const { db } = await import('../lib/firebase.js')
    await update(ref(db, `qualifyingEvents/${eventId}/teams/${team.id}`), { approved: next })
  }
  // No color — plain black, like the reference. (Screen-only controls carry no
  // color either.) The printed sheet is rendered separately and omits controls.
  return (
    <>
      <div
        className="grid grid-cols-12 gap-1 px-3 py-2 text-sm items-center border-t"
        style={{ borderColor: 'rgba(0,0,0,0.12)', background: '#FFFFFF' }}>
        {/* Position */}
        <div className="col-span-1 font-mono font-extrabold text-sm" style={{ color: '#111827' }}>
          {position}
        </div>
        {/* Players — names only (no team number), with a small out-of-the-way flag */}
        <div className="col-span-5 min-w-0" style={{ color: '#111827' }}>
          <span className="font-semibold">
            {[team.player1, team.player2].filter(Boolean).join(' / ') || '—'}
          </span>
          {totalOnly ? (
            <span
              className="ml-2 align-middle text-sm font-bold uppercase tracking-wider rounded px-1 py-0.5 whitespace-nowrap"
              style={{ background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}
              title="Total score entered — hole-by-hole scorecard not filled in">
              Total only
            </span>
          ) : null}
        </div>
        {/* Scorecard — its own compact control */}
        <div className="col-span-2 text-center">
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="text-sm font-extrabold uppercase tracking-widest rounded-md px-2 py-1"
            style={{ color: '#111827', background: '#FFFFFF', border: '1px solid #9CA3AF' }}
            title={expanded ? 'Hide scorecard' : 'View scorecard'}>
            {expanded ? '▲ Hide' : '▼ View'}
          </button>
        </div>
        {/* Approved — its own column, before To Par */}
        <div className="col-span-1 text-center">
          <input
            type="checkbox"
            checked={!!team.approved}
            onChange={onToggleApproved}
            disabled={closed}
            aria-label={`Approve ${team.teamName}`}
          />
        </div>
        {/* To Par Gross */}
        <div className="col-span-1 text-right font-mono font-bold" style={{ color: '#111827' }}>
          {toPar}
        </div>
        {/* Total Gross */}
        <div className="col-span-2 text-right font-mono font-extrabold" style={{ color: '#111827' }}>
          {team.score}
        </div>
      </div>
      {expanded ? (
        <ScorecardStrip team={team} eventId={eventId} />
      ) : null}
    </>
  )
}

// Read-only hole-by-hole grid. Same shape the player sees on the public
// /qualify page during scoring — 9 columns per row, two rows for OUT/IN.
// Holes that haven't been scored render an em-dash so the admin can see
// at a glance which holes are still pending.
//
// Cell color follows score-vs-par:
//   ≤ -2 (eagle+) → blue
//      -1 (birdie) → red
//       0 (par)    → unchanged (default white card)
//      +1 (bogey)  → yellow
//   ≥ +2 (double+) → orange
// Standalone qualifying events aren't tied to a tournament so par per
// hole comes from DEFAULT_PARS (CLUB.course.pars), same fallback the
// public /qualify scorecard uses when info.pars isn't set.
function scoreColor(diff) {
  // Bold solid fills; the hole NUMBER is always BLACK (never white), every cell.
  if (diff <= -2) return { bg: '#2563EB', text: '#111827', border: '#1D4ED8' } // eagle or better — blue
  if (diff === -1) return { bg: '#DC2626', text: '#111827', border: '#B91C1C' } // birdie — red
  if (diff === 1)  return { bg: '#EAB308', text: '#111827', border: '#CA8A04' } // bogey — yellow
  if (diff >= 2)   return { bg: '#F97316', text: '#111827', border: '#EA580C' } // double or worse — orange
  return null // par — white bg, black number
}

// EDITABLE manual-entry scorecard. Staff type hole-by-hole (which auto-sums the
// total) and/or a total directly, then Save — which posts score + holes +
// submittedAt to the team via submitQualifyingEventResult, so the SCORE column
// updates live. Every field is dark-on-white and fully editable (not read-only).
function ScorecardStrip({ team, eventId }) {
  const initHoles = () => {
    const src = team?.holes || {}
    const out = {}
    for (let h = 1; h <= 18; h++) {
      const raw = src[h] ?? src[String(h)]
      const n = Number(raw)
      out[h] = Number.isFinite(n) && n > 0 ? String(n) : ''
    }
    return out
  }
  const [holes, setHoles] = useState(initHoles)
  const [total, setTotal] = useState(team?.score != null && Number(team.score) > 0 ? String(team.score) : '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const sumHoles = useMemo(() => {
    let s = 0, any = false
    for (let h = 1; h <= 18; h++) { const n = Number(holes[h]); if (Number.isFinite(n) && n > 0) { s += n; any = true } }
    return any ? s : null
  }, [holes])

  // Editing a hole auto-fills the total from the running sum (staff entering
  // hole-by-hole never have to add it up). They can still type the total
  // directly when they only know the final number.
  const setHole = (h, v) => {
    const clean = v.replace(/[^0-9]/g, '').slice(0, 2)
    setHoles((prev) => {
      const next = { ...prev, [h]: clean }
      let s = 0, any = false
      for (let i = 1; i <= 18; i++) { const n = Number(next[i]); if (Number.isFinite(n) && n > 0) { s += n; any = true } }
      if (any) setTotal(String(s))
      return next
    })
  }

  const save = async () => {
    const scoreNum = Number(total)
    if (!Number.isFinite(scoreNum) || scoreNum <= 0) { setMsg('Enter a total (or hole scores) first.'); return }
    setBusy(true); setMsg('')
    try {
      const holesOut = {}
      for (let h = 1; h <= 18; h++) { const n = Number(holes[h]); if (Number.isFinite(n) && n > 0) holesOut[h] = n }
      await submitQualifyingEventResult(eventId, team.id, { score: scoreNum, holes: holesOut, teamName: team.teamName })
      setMsg('✓ Saved — score posted.')
    } catch (e) {
      setMsg(e?.message || 'Save failed.')
    }
    setBusy(false)
  }

  return (
    <div
      className="border-t px-3 py-3"
      style={{ borderColor: 'rgba(18, 61, 38,0.20)', background: '#FFFFFF' }}>
      <p className="text-sm uppercase tracking-widest font-extrabold mb-2" style={{ color: PRIMARY }}>
        Enter Scorecard · {team.teamName}
      </p>
      {/* One-line color key — same palette as the hole cells. */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-bold mb-3"
        style={{ color: PRIMARY }}>
        <LegendChip color="#1D4ED8" bg="#2563EB" border="#1D4ED8" label="Eagle or better" />
        <LegendChip color="#B91C1C" bg="#DC2626" border="#B91C1C" label="Birdie" />
        <LegendChip color={PRIMARY} bg="#FFFFFF" border={`${PRIMARY}55`} label="Par" />
        <LegendChip color="#A16207" bg="#EAB308" border="#CA8A04" label="Bogey" />
        <LegendChip color="#C2410C" bg="#F97316" border="#EA580C" label="Double or worse" />
      </div>
      <div className="grid grid-cols-9 gap-1">
        {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
          const par = Number(DEFAULT_PARS[h - 1]) || 4
          const n = Number(holes[h])
          const has = Number.isFinite(n) && n > 0
          const color = has ? scoreColor(n - par) : null
          const bg = color?.bg || '#FFFFFF'
          const border = color?.border || `${PRIMARY}40`
          const text = color?.text || '#111827' // par/empty — black number on white
          return (
            <div
              key={h}
              className="rounded-md py-1 text-center"
              style={{ background: bg, border: `1px solid ${border}` }}>
              <div className="text-sm font-black" style={{ color: '#111827' }}>H{h}</div>
              <input
                type="text"
                inputMode="numeric"
                value={holes[h]}
                onChange={(e) => setHole(h, e.target.value)}
                placeholder="–"
                aria-label={`Hole ${h} score`}
                className="w-full text-center text-lg font-black font-mono bg-transparent outline-none"
                style={{ color: text }}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col">
          <span className="text-sm uppercase tracking-widest font-extrabold mb-1" style={{ color: PRIMARY }}>Total score</span>
          <input
            type="text"
            inputMode="numeric"
            value={total}
            onChange={(e) => setTotal(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
            placeholder="e.g. 72"
            aria-label="Total score"
            className="rounded-lg px-3 py-2 text-lg font-extrabold font-mono w-28 outline-none"
            style={{ color: PRIMARY, background: '#FFFFFF', border: `2px solid ${PRIMARY}` }}
          />
        </label>
        {sumHoles != null ? (
          <button
            type="button"
            onClick={() => setTotal(String(sumHoles))}
            className="text-sm font-bold rounded-md px-2 py-1"
            style={{ color: PRIMARY, background: '#FFFFFF', border: `1px solid ${PRIMARY}` }}
            title="Set total to the sum of the holes entered">
            = sum of holes ({sumHoles})
          </button>
        ) : null}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-sm font-extrabold uppercase tracking-widest disabled:opacity-60"
          style={{ color: '#FFFFFF', background: PRIMARY, border: `2px solid ${PRIMARY}` }}>
          {busy ? 'Saving…' : (team.submittedAt ? '💾 Update Score' : '💾 Save Score')}
        </button>
        {msg ? <span className="text-sm font-bold" style={{ color: msg.startsWith('✓') ? '#15803D' : '#B91C1C' }}>{msg}</span> : null}
      </div>
      {team.submittedAt ? (
        <p className="text-sm mt-2 font-semibold" style={{ color: PRIMARY }}>
          Posted total {team.score} · last submitted {new Date(team.submittedAt).toLocaleString()}
        </p>
      ) : (
        <p className="text-sm italic mt-2" style={{ color: '#374151' }}>
          No score posted yet — type the holes and/or a total, then Save.
        </p>
      )}
    </div>
  )
}

// Color-key chip used by ScorecardStrip's legend row. Same palette the
// cells below it use so the eye maps from chip → grid one-to-one.
function LegendChip({ color, bg, border, label }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 18, height: 18, borderRadius: 4,
          background: bg,
          border: `1.5px solid ${border}`,
        }}
      />
      <span style={{ color }}>{label}</span>
    </span>
  )
}

// "Create Tournament from This List" — two-step flow:
//
//   STEP 1 (form): admin fills name/date/format/course AND picks a
//   qualifying event from a "Qualifying List" dropdown (defaults to the
//   one they came from). Submit creates the tournament, reads the
//   selected event's approved teams, and writes them into
//   /tournaments/{newId}/registrations via bulkAddRegistrations.
//
//   STEP 2 (review): same modal shell, new content — title flips to
//   "Tournament Created — Ready to Build" and shows a numbered roster
//   of the approved teams sorted by qualifying score. Two buttons:
//   SET UP WITH AI dispatches the global ai-prefill window event after
//   navigation so the floating ✨ AI Assistant on the tournament page
//   opens with a primed prompt; SET UP MANUALLY just navigates.
function CreateTournamentChoiceModal({ eventId, info, submittedCount, teamsMap, onClose }) {
  const navigate = useNavigate()

  // Subscribe to every qualifying event for the dropdown. The list shows
  // each event's name + count of approved teams so the admin can pick
  // the right one at a glance.
  const [allEvents, setAllEvents] = useState({})
  useEffect(() => {
    const off = watchQualifyingEvents(setAllEvents)
    return () => off()
  }, [])

  const eventOptions = useMemo(() => {
    return Object.entries(allEvents || {}).map(([id, v]) => {
      const teams = v?.teams || {}
      const approved = Object.values(teams).filter((t) => t && t.approved === true).length
      return {
        id,
        name: v?.info?.name || '(unnamed)',
        approved,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))
  }, [allEvents])

  // Form state — pre-fill from the qualifying event the admin came from.
  // selectedEventId is the live source for the approved-team read at
  // submit time, so changing the dropdown changes which event's teams
  // get carried into the new tournament.
  const today = new Date().toISOString().slice(0, 10)
  const [selectedEventId, setSelectedEventId] = useState(eventId)
  const [name, setName] = useState(info?.name || 'Tournament')
  const [course, setCourse] = useState(CLUB.course?.name || '')
  const [date, setDate] = useState(today)
  const [format, setFormat] = useState(info?.format || QE_FORMATS[0])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [progress, setProgress] = useState('')

  // Step 2 state — populated after the create succeeds. tournamentId
  // drives the SET UP buttons' navigation target.
  const [step, setStep] = useState('form') // 'form' | 'created'
  const [createdTournamentId, setCreatedTournamentId] = useState(null)
  const [createdApprovedTeams, setCreatedApprovedTeams] = useState([])

  // Approved count for the currently selected event (drives the button
  // label + warning copy). When the dropdown changes, this updates.
  const selectedApprovedCount = useMemo(() => {
    const opt = eventOptions.find((o) => o.id === selectedEventId)
    return opt ? opt.approved : 0
  }, [eventOptions, selectedEventId])

  const submit = async () => {
    setBusy(true); setErr(''); setProgress('Creating tournament…')
    try {
      // Step 1: create the tournament.
      const tournamentId = await createTournament({
        name: name.trim(),
        course: course.trim(),
        date,
        format,
      })
      console.log('[QE→Tournament] created tournament', tournamentId)

      // Step 2: re-read approved teams from the SELECTED qualifying
      // event (not necessarily the eventId prop — the dropdown can
      // point at a different one).
      setProgress('Reading approved qualifying teams…')
      const snap = await get(ref(db, `qualifyingEvents/${selectedEventId}/teams`))
      const map = snap.val() || {}
      const approved = Object.values(map).filter((t) => t && t.approved === true)
      console.log('[QE→Tournament] approved teams read from Firebase:', approved.length, approved)

      // Step 3: map to registration rows + bulk insert. Strip
      // undefined fields so Firebase doesn't reject them.
      const rows = approved.map((t) => {
        const teamName = String(t.teamName || '').trim() || 'Team'
        const players = [t.player1, t.player2]
          .filter((p) => p && String(p).trim().length > 0)
          .map((p) => String(p).trim())
        const qs = Number.isFinite(Number(t.qualifyingScore))
          ? Number(t.qualifyingScore)
          : (Number.isFinite(Number(t.score)) ? Number(t.score) : null)
        const row = { teamName, qualifyingStatus: 'complete' }
        if (players.length > 0) row.players = players
        if (qs != null) row.qualifyingScore = qs
        return row
      })
      console.log('[QE→Tournament] registration rows to write:', rows.length, rows.slice(0, 3))

      if (rows.length > 0) {
        setProgress(`Adding ${rows.length} teams to tournament…`)
        const written = await bulkAddRegistrations(tournamentId, rows)
        console.log('[QE→Tournament] bulkAddRegistrations returned:', written)
      }

      // Step 4: verify the write landed.
      setProgress('Verifying teams were written…')
      const regSnap = await get(ref(db, `tournaments/${tournamentId}/registrations`))
      const regMap = regSnap.val() || {}
      const regCount = Object.keys(regMap).length
      console.log('[QE→Tournament] verification read /registrations count:', regCount, regMap)
      if (rows.length > 0 && regCount === 0) {
        throw new Error(`Wrote ${rows.length} rows but verification read found 0 registrations. Tournament ID ${tournamentId}.`)
      }

      // Step 5: hand off to STEP 2. Stash the team list (sorted lowest
      // score first — best qualifier on top) for the roster modal and
      // flip the step.
      const sorted = [...approved].sort((a, b) => {
        const sa = Number(a.qualifyingScore ?? a.score ?? 0)
        const sb = Number(b.qualifyingScore ?? b.score ?? 0)
        return sa - sb
      })
      setCreatedTournamentId(tournamentId)
      setCreatedApprovedTeams(sorted)
      setStep('created')
      setBusy(false)
      setProgress('')
    } catch (e) {
      console.error('[QE→Tournament] failed:', e)
      setErr(e?.message || 'Create failed.')
      setBusy(false)
      setProgress('')
    }
  }

  const ready = name.trim().length > 0 && course.trim().length > 0 && date.length > 0 && selectedEventId

  if (step === 'created') {
    // Look up the selected qualifying event's display name (the
    // dropdown value may have changed between mount and submit).
    const qen = allEvents?.[selectedEventId]?.info?.name || ''
    return (
      <TournamentCreatedRosterModal
        tournamentId={createdTournamentId}
        approvedTeams={createdApprovedTeams}
        tournamentName={name.trim()}
        qualifyingEventName={qen}
        onClose={onClose}
      />
    )
  }

  // STEP 1: the create form (now with Qualifying List dropdown)
  return (
    <Modal title="Create Tournament from List" onClose={busy ? () => {} : onClose}>
      <p className="text-sm" style={{ color: PRIMARY }}>
        Build a new tournament and carry over <b>{selectedApprovedCount}</b> approved {selectedApprovedCount === 1 ? 'team' : 'teams'} from the selected qualifying list.
      </p>
      {selectedApprovedCount === 0 ? (
        <p className="text-sm mt-2" style={{ color: '#B45309' }}>
          ⚠ No teams have been approved on the selected qualifying list. Approve some first, or this will create an empty tournament.
        </p>
      ) : null}

      <div className="space-y-3 mt-4">
        <Field label="Qualifying List">
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="input"
            disabled={busy}>
            {eventOptions.length === 0 ? (
              <option value="">No qualifying events found</option>
            ) : null}
            {eventOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.approved} approved
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tournament Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            disabled={busy}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
              disabled={busy}
            />
          </Field>
          <Field label="Format">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="input"
              disabled={busy}>
              {QE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Course">
          <input
            type="text"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            className="input"
            disabled={busy}
          />
        </Field>
      </div>

      {progress ? <p className="text-sm mt-2" style={{ color: PRIMARY }}>{progress}</p> : null}
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}

      <div className="flex gap-2 justify-end mt-4">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={busy || !ready}>
          {busy ? 'Working…' : `Create Tournament${selectedApprovedCount > 0 ? ` (${selectedApprovedCount} ${selectedApprovedCount === 1 ? 'team' : 'teams'})` : ''}`}
        </button>
      </div>
    </Modal>
  )
}

// Shared shell — Back + Home buttons up top, white card body. Used by
// both the loaded detail view and the loading/not-found fallbacks so
// every state still gives the admin a way out.
function DetailShell({ children }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#123D26' }}>
      {/* One subtle back-into-the-flow link — this is the next step of the PBCC
          4-Ball flow, not a standalone page, so no Back-to-Admin/Home chrome.
          Moving forward happens via "Submit to Tournament" (rolls to State 3). */}
      <header className="px-4 py-3">
        <Link
          to="/admin/pbcc-4ball"
          className="text-sm font-extrabold uppercase tracking-widest"
          style={{ color: ACCENT, textDecoration: 'none' }}>
          ← PBCC 4-Ball
        </Link>
      </header>
      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        <div
          className="rounded-2xl p-5 sm:p-6"
          style={{ background: '#FFFFFF', border: `1px solid ${ACCENT}30` }}>
          {children}
        </div>
      </main>
      <AIAssistant tournamentId={null} />
    </div>
  )
}

// Step-2 modal — exported so AdminApp.jsx's CreateModal flow can reuse
// the same roster + setup-path UI after it imports approved teams from
// a qualifying event. Self-contained: takes a tournamentId and the
// approved-teams array, renders the numbered roster sorted by
// qualifying score, and handles the two SET UP buttons.
//
// tournamentName + qualifyingEventName are stitched into the
// ai-prefill payload so the AI Assistant gets full context ("I just
// created PBCC 4-Ball 2026 from the PBCC 4-Ball Qualifier …") instead
// of a bare "set up this tournament" command.
export function TournamentCreatedRosterModal({
  tournamentId,
  approvedTeams = [],
  tournamentName = '',
  qualifyingEventName = '',
  onClose,
  onBuild = null,        // optional async: run BEFORE navigating (e.g. promote the node)
  titleText = 'Tournament Created — Ready to Build',
  introText = null,
}) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  // Defensive sort — caller usually pre-sorts, but the roster modal
  // should never trust input order. Lowest qualifying score first.
  const sorted = useMemo(() => {
    return [...(approvedTeams || [])].sort((a, b) => {
      const sa = Number(a?.qualifyingScore ?? a?.score ?? 0)
      const sb = Number(b?.qualifyingScore ?? b?.score ?? 0)
      return sa - sb
    })
  }, [approvedTeams])

  // When onBuild is provided (the New-Tournament promote flow), run it first —
  // that promotes the same node into the live tournament — then proceed to the
  // AI / manual setup exactly as before. Returns false if the build failed.
  const runBuild = async () => {
    if (!onBuild) return true
    setBusy(true)
    try { await onBuild(); return true }
    catch (e) { setBusy(false); window.alert(e?.message || 'Build failed.'); return false }
  }

  const onSetUpManually = async () => {
    if (!(await runBuild())) return
    onClose()
    // Hard redirect — same rationale as Set Up with AI below: forces a
    // clean React tree rebuild so AdminApp mounts TournamentRoute from
    // scratch instead of relying on in-place route swap.
    window.location.href = `/admin/t/${tournamentId}`
  }
  const onSetUpWithAI = async () => {
    if (!(await runBuild())) return
    // onBuild flow (the PBCC Submit-to-Tournament / promote path): the
    // deterministic build ALREADY ran in onBuild, so the tournament is fully
    // built. Land on it with the AI assistant CLOSED — no auto-opened chat, no
    // pre-filled prompt. The admin opens the assistant themselves if they want
    // to make adjustments. Hard redirect so AdminApp remounts cleanly.
    if (onBuild) {
      onClose()
      window.location.href = `/admin/t/${tournamentId}`
      return
    }
    // Legacy flow (no onBuild): the tournament exists but its flights are NOT
    // built yet — here the AI itself does the build, so it still needs the
    // primed auto-send command on the destination screen.
    const teamCount = sorted.length
    const tn = tournamentName?.trim() || 'this tournament'
    const qen = qualifyingEventName?.trim() || 'the selected qualifying list'
    const prefill = {
      command:
        `Tournament already created. Tournament ID: ${tournamentId}. ` +
        `Tournament name: ${tn}. ` +
        `Imported from ${qen} qualifying list with ${teamCount} teams sorted by qualifying score. ` +
        `Do not create a new tournament. ` +
        `Please build the flights and set up the full tournament now.`,
      autoSend: true,
    }
    try {
      sessionStorage.setItem('ai-prefill', JSON.stringify(prefill))
    } catch {}
    onClose()
    window.location.href = `/admin/t/${tournamentId}`
  }

  return (
    <Modal title={titleText} onClose={onClose}>
      <p className="text-sm" style={{ color: PRIMARY }}>
        {introText || <>Your tournament was created with <b>{sorted.length}</b> approved {sorted.length === 1 ? 'team' : 'teams'} from the qualifying list. Pick a setup path below.</>}
      </p>

      <div
        className="mt-4 rounded-xl p-3"
        style={{ background: '#FFFFFF', border: `1px solid ${ACCENT}55`, maxHeight: 320, overflowY: 'auto' }}>
        <p className="text-sm uppercase tracking-widest font-extrabold mb-2" style={{ color: ACCENT }}>
          Field — {sorted.length} team{sorted.length === 1 ? '' : 's'} (sorted by qualifying score)
        </p>
        <ol className="text-sm space-y-1 list-decimal list-inside" style={{ color: PRIMARY }}>
          {sorted.map((t, i) => {
            const score = Number(t?.qualifyingScore ?? t?.score ?? null)
            return (
              <li key={`${t?.teamName || 'team'}-${i}`}>
                <span className="font-bold">{t?.teamName || 'Team'}</span>
                {Number.isFinite(score) ? (
                  <span className="font-mono" style={{ color: 'rgba(18, 61, 38,0.65)' }}> · {score}</span>
                ) : null}
              </li>
            )
          })}
        </ol>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onSetUpWithAI}
          disabled={busy}
          className="rounded-lg py-3 px-4 font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-60"
          style={{ background: PRIMARY, color: ACCENT, border: `1.5px solid ${ACCENT}` }}>
          {busy ? 'Building…' : '✦ Build with AI'}
        </button>
        <button
          type="button"
          onClick={onSetUpManually}
          disabled={busy}
          className="rounded-lg py-3 px-4 font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-60"
          style={{ background: ACCENT, color: '#123D26', border: '1.5px solid #123D26' }}>
          {busy ? 'Building…' : '🔧 Build Manually'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mt-3 leading-snug">
        AI: opens the floating ✦ AI Assistant on the new tournament page with a primed prompt.
        Manual: jumps straight into the tournament admin so you can configure flights, tee times, and brackets yourself.
      </p>
    </Modal>
  )
}
